import { isDefined } from "@clipboard-health/util-ts";
import { Skeleton, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { useReferralStats } from "../../api/useReferralStats";
import { WorkplaceReferralRouterPath } from "../../paths";
import { RefereeType, ReferralConversionStage } from "../../types";
import { ReferralStatItem } from "./ReferralStatItem";

interface ReferralStatProps {
  agentId: string;
}

const LOADING_SKELETON_HEIGHT = 80;

export function ReferralStats({ agentId }: ReferralStatProps) {
  const { data: referralStatsData, isLoading } = useReferralStats({
    referrerId: agentId,
    type: RefereeType.WORKER,
  });

  return (
    <Stack>
      <Stack spacing={4}>
        {isLoading ? (
          <Skeleton
            variant="rounded"
            height={LOADING_SKELETON_HEIGHT}
            role="progressbar"
            aria-label="Loading total earnings"
          />
        ) : (
          isDefined(referralStatsData) && (
            <ReferralStatItem
              showInfoIcon
              title="Total Earnings"
              formattedValue={formatDollarsAsUsd(referralStatsData.totalEarnings, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              })}
              formattedValueLinkPath={`${WorkplaceReferralRouterPath.WORKPLACE_REFERRALS_STATS_PATH}?selectedTab=${ReferralConversionStage.CONVERTED}`}
            />
          )
        )}

        <Stack direction="row" spacing={2} width="100%">
          {isLoading ? (
            <>
              <Skeleton
                variant="rounded"
                width="50%"
                height={LOADING_SKELETON_HEIGHT}
                role="progressbar"
                aria-label="Loading pending referrals"
              />
              <Skeleton
                variant="rounded"
                width="50%"
                height={LOADING_SKELETON_HEIGHT}
                role="progressbar"
                aria-label="Loading successful referrals"
              />
            </>
          ) : (
            isDefined(referralStatsData) && (
              <>
                <ReferralStatItem
                  title="Pending Referrals"
                  formattedValueLinkPath={`${WorkplaceReferralRouterPath.WORKPLACE_REFERRALS_STATS_PATH}?selectedTab=${ReferralConversionStage.UNCONVERTED}`}
                  formattedValue={referralStatsData.unconvertedReferees.toString()}
                  sx={(theme) => ({
                    flex: 1,
                    background: theme.palette.workplaceReferrals?.stats.pending.gradient,
                  })}
                />
                <ReferralStatItem
                  title="Successful Referrals"
                  formattedValueLinkPath={`${WorkplaceReferralRouterPath.WORKPLACE_REFERRALS_STATS_PATH}?selectedTab=${ReferralConversionStage.CONVERTED}`}
                  formattedValue={referralStatsData.convertedReferees.toString()}
                  sx={(theme) => ({
                    flex: 1,
                    background: theme.palette.workplaceReferrals?.stats.success.gradient,
                  })}
                />
              </>
            )
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
